import { useState, useRef } from "react";
import { CheckCircle2, AlertCircle, Upload, X, ImagePlus } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export const SERVICE_OPTIONS = [
  "Excavation",
  "Concrete & Foundations",
  "Site Prep & Grading",
  "Decorative & Stamped Concrete",
  "Retaining Walls",
  "Driveways, Paving & Surfacing",
  "Drainage",
  "Other / Not Sure",
];

const MAX_PHOTOS = 8;
const MAX_PHOTO_MB = 15;
const inputCls = "w-full bg-white border-2 border-[#2C3539]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3 text-[#2C3539] text-sm md:text-base font-medium";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">{label}</span>
      {children}
    </label>
  );
}

export function BookingForm({ defaultService = "Excavation", heading = "Request a quote" }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: defaultService,
    preferred_date: "", location: "", message: "",
  });
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState("idle"); // idle, sending, sent, error
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = [];
    for (const f of incoming) {
      if (photos.length + accepted.length >= MAX_PHOTOS) break;
      if (!f.type.startsWith("image/")) continue;
      if (f.size > MAX_PHOTO_MB * 1024 * 1024) {
        setError(`${f.name} is larger than ${MAX_PHOTO_MB}MB and was skipped.`);
        continue;
      }
      accepted.push({ file: f, preview: URL.createObjectURL(f), id: `${f.name}-${f.size}-${Math.random()}` });
    }
    if (accepted.length) setPhotos((prev) => [...prev, ...accepted]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (id) => {
    setPhotos((prev) => {
      const p = prev.find((x) => x.id === id);
      if (p) URL.revokeObjectURL(p.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
    photos.forEach(({ file }) => fd.append("photos", file));
    try {
      await api.post("/bookings", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStatus("sent");
      setForm({ name: "", email: "", phone: "", service: defaultService, preferred_date: "", location: "", message: "" });
      photos.forEach((p) => URL.revokeObjectURL(p.preview));
      setPhotos([]);
    } catch (err) {
      setStatus("error");
      setError(formatApiError(err));
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="booking-form"
      className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-10"
    >
      <div className="font-display text-3xl md:text-4xl uppercase text-[#2C3539]">{heading}</div>
      <p className="mt-2 text-[#2C3539]/70 text-sm">Free estimates · No obligation · Add site photos for the most accurate quote.</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full Name *">
          <input data-testid="booking-name" required value={form.name} onChange={handle("name")} className={inputCls} />
        </Field>
        <Field label="Email *">
          <input data-testid="booking-email" type="email" required value={form.email} onChange={handle("email")} className={inputCls} />
        </Field>
        <Field label="Phone *">
          <input data-testid="booking-phone" required value={form.phone} onChange={handle("phone")} className={inputCls} />
        </Field>
        <Field label="Service Needed *">
          <select data-testid="booking-service" required value={form.service} onChange={handle("service")} className={inputCls}>
            {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Preferred Start Date">
          <input data-testid="booking-date" type="date" value={form.preferred_date} onChange={handle("preferred_date")} className={inputCls} />
        </Field>
        <Field label="Job Location (City)">
          <input data-testid="booking-location" value={form.location} onChange={handle("location")} className={inputCls} placeholder="e.g. Nanaimo, BC" />
        </Field>
        <div className="md:col-span-2">
          <Field label="Project Details *">
            <textarea data-testid="booking-message" required rows={5} value={form.message} onChange={handle("message")} className={inputCls} placeholder="Tell us about scope, timing, access, or any plans you have." />
          </Field>
        </div>

        {/* Photo upload */}
        <div className="md:col-span-2">
          <label className="block">
            <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">
              Site Photos (optional · up to {MAX_PHOTOS})
            </span>
            <div
              data-testid="booking-photo-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
              className="cursor-pointer border-2 border-dashed border-[#2C3539]/30 hover:border-[#2F5F80] bg-[#F1F3F5] hover:bg-[#2F5F80]/5 transition-colors px-6 py-8 text-center"
            >
              <div className="flex flex-col items-center gap-2 text-[#2C3539]">
                <ImagePlus size={28} className="text-[#2F5F80]"/>
                <div className="font-display text-lg uppercase">Drop photos here or click to choose</div>
                <div className="text-xs text-[#2C3539]/60">Up to {MAX_PHOTOS} photos · JPEG/PNG/WEBP · {MAX_PHOTO_MB}MB each</div>
              </div>
              <input
                ref={fileRef}
                data-testid="booking-photo-input"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => addFiles(e.target.files)}
                className="hidden"
              />
            </div>
          </label>

          {photos.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="booking-photo-previews">
              {photos.map((p) => (
                <div key={p.id} className="relative group border-2 border-[#2C3539]/10">
                  <img src={p.preview} alt={p.file.name} className="w-full h-28 object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(p.id)}
                    data-testid={`booking-photo-remove-${p.id}`}
                    className="absolute top-1 right-1 bg-[#2C3539] hover:bg-[#2F5F80] text-white p-1"
                    aria-label="Remove photo"
                  >
                    <X size={14}/>
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-[#2C3539]/85 text-white text-[10px] px-1.5 py-1 truncate">
                    {p.file.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {status === "error" && (
        <div data-testid="booking-error" className="mt-5 bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-700 inline-flex items-start gap-2">
          <AlertCircle size={18} className="mt-0.5"/> {error || "Something went wrong."}
        </div>
      )}
      {status === "sent" && (
        <div data-testid="booking-success" className="mt-5 bg-[#2F5F80]/10 border-l-4 border-[#2F5F80] px-4 py-3 text-sm text-[#2C3539] inline-flex items-start gap-2">
          <CheckCircle2 size={18} className="text-[#2F5F80] mt-0.5"/> Request received — we'll be in touch within one business day.
        </div>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        data-testid="booking-submit"
        className="mt-8 inline-flex items-center gap-3 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-8 py-4 uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Upload size={18}/> {status === "sending" ? "Sending…" : "Send Request"}
      </button>
    </form>
  );
}
